import type { AssetClass } from "@repo/shared/types";
import { AlertCircle, Loader2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  useCorrelationMatrix,
  useFetchApiCorrelations,
  useSaveCorrelations,
} from "@/lib/hooks/use-portfolio-collections";
import { useLocalize } from "@/lib/i18n/use-localize";
import { useT } from "@/lib/i18n/use-t";
import { cn } from "@/lib/utils";
import { buildCorrelationCsv, downloadCsv, parseCorrelationCsv } from "./correlation-csv";
import { T } from "./correlation-matrix.i18n";
import { buildCorrelationPayload } from "./correlation-payload";

/** A correlation cell is valid when empty (null) or a finite number within [-1, 1]. */
const isInRange = (v: number | null): boolean =>
  v === null || (Number.isFinite(v) && v >= -1 && v <= 1);

type CorrelationMatrixData = Record<string, Record<string, number | null>>;

// QP-164 — above this many asset classes, the full grid is unwieldy (and
// grows quadratically), so it's replaced by a status summary. Matches the
// ticket's threshold; mirrored for products once a product correlation
// matrix exists.
const COLLAPSE_THRESHOLD = 10;

// Colors for asset classes - using chart colors from CSS variables
const ASSET_CLASS_COLORS = ["bg-chart-1", "bg-chart-2", "bg-chart-3", "bg-chart-4", "bg-chart-5"];

type CorrelationMatrixProps = {
  portfolioCollectionId: string;
  assetClasses: AssetClass[];
  currency: string;
};

export function CorrelationMatrix({
  portfolioCollectionId,
  assetClasses,
  currency,
}: CorrelationMatrixProps) {
  const localize = useLocalize();
  const t = useT(T);

  const { correlations: initialCorrelations, isLoading: loading } =
    useCorrelationMatrix(portfolioCollectionId);

  const saveCorrelations = useSaveCorrelations();
  const fetchApiCorrelations = useFetchApiCorrelations();

  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  // API correlations (from the correlation API)
  const [apiCorrelations, setApiCorrelations] = useState<CorrelationMatrixData>({});
  const [apiError, setApiError] = useState<string | null>(null);

  // Stored correlations (from database) — an editable local copy, resynced
  // whenever the query's data actually changes (React Query's structural
  // sharing keeps the same reference across a refetch that returns identical
  // data, so this doesn't fire on every background revalidation — only when
  // the server-side values genuinely changed, e.g. a calculations-mode
  // switch wiping the matrix elsewhere).
  const [storedCorrelations, setStoredCorrelations] = useState<CorrelationMatrixData>({});

  useEffect(() => {
    if (!loading) {
      setStoredCorrelations(initialCorrelations as CorrelationMatrixData);
    }
  }, [initialCorrelations, loading]);

  // Track which asset classes have linked instruments
  const linkedAssetClasses = assetClasses.filter((ac) => ac.ticker && ac.dataset);

  // Fetch API correlations
  const handleFetchApiCorrelations = () => {
    setApiError(null);

    fetchApiCorrelations.mutate(
      {
        collectionId: portfolioCollectionId,
        data: { currencyIso: currency },
      },
      {
        onSuccess: (result) => {
          setApiCorrelations(result.matrix);
        },
        onError: (err: Error) => {
          setApiError(err.message || t.failedToFetch);
          setApiCorrelations({});
        },
      },
    );
  };

  // Copy all API values to stored values (rounded to 2 decimals)
  const handleCopyAllFromApi = () => {
    // Deep copy to avoid mutating existing state
    const newStored: CorrelationMatrixData = {};
    for (const [fromId, toCorrs] of Object.entries(storedCorrelations)) {
      newStored[fromId] = { ...toCorrs };
    }

    for (const [fromId, toCorrs] of Object.entries(apiCorrelations)) {
      if (!newStored[fromId]) {
        newStored[fromId] = {};
      }
      for (const [toId, value] of Object.entries(toCorrs)) {
        if (value !== null && value !== undefined) {
          newStored[fromId][toId] = Math.round(value * 100) / 100;
        }
      }
    }

    setStoredCorrelations(newStored);
  };

  // Copy only empty stored values from API (rounded to 2 decimals)
  const handleCopyEmptyFromApi = () => {
    // Deep copy to avoid mutating existing state
    const newStored: CorrelationMatrixData = {};
    for (const [fromId, toCorrs] of Object.entries(storedCorrelations)) {
      newStored[fromId] = { ...toCorrs };
    }

    for (const [fromId, toCorrs] of Object.entries(apiCorrelations)) {
      if (!newStored[fromId]) {
        newStored[fromId] = {};
      }
      for (const [toId, value] of Object.entries(toCorrs)) {
        if (value !== null && value !== undefined) {
          // Only copy if stored value is empty/null
          if (newStored[fromId][toId] === null || newStored[fromId][toId] === undefined) {
            newStored[fromId][toId] = Math.round(value * 100) / 100;
          }
        }
      }
    }

    setStoredCorrelations(newStored);
  };

  // Handle individual correlation change
  const handleCorrelationChange = (fromId: string, toId: string, value: string) => {
    const numValue = value === "" ? null : parseFloat(value);

    setStoredCorrelations((prev) => {
      // Deep copy to avoid mutating existing state
      const newMatrix: CorrelationMatrixData = {};
      for (const [fId, toCorrs] of Object.entries(prev)) {
        newMatrix[fId] = { ...toCorrs };
      }
      if (!newMatrix[fromId]) {
        newMatrix[fromId] = {};
      }
      newMatrix[fromId][toId] = numValue;
      return newMatrix;
    });
  };

  // Get correlation value for display
  const getCorrelation = (
    matrix: CorrelationMatrixData,
    fromId: string,
    toId: string,
  ): number | null => {
    return matrix[fromId]?.[toId] ?? null;
  };

  // Save correlations
  const handleSave = () => {
    setError(null);
    setSuccess(false);

    // Validate entered values before persisting — block save on out-of-range input.
    const hasInvalid = Object.values(storedCorrelations).some((toMap) =>
      Object.values(toMap).some((v) => !isInRange(v)),
    );
    if (hasInvalid) {
      setError(t.valuesOutOfRange);
      return;
    }

    saveCorrelations.mutate(
      {
        collectionId: portfolioCollectionId,
        data: {
          matrix: buildCorrelationPayload(
            assetClasses.map((e) => e.id),
            storedCorrelations,
          ),
        },
      },
      {
        onSuccess: () => {
          setSuccess(true);
          setSuccessMessage(t.correlationsSaved);
          // Clear success message after 3 seconds
          setTimeout(() => setSuccess(false), 3000);
        },
        onError: (err: Error) => {
          setError(err.message || t.failedToSave);
        },
      },
    );
  };

  // QP-164 — CSV export/import. Names are the row/column identity; ";" is
  // the delimiter (asset class names are already restricted from containing
  // it), so a name collision within the current asset classes would make
  // the CSV ambiguous — block CSV entirely in that case.
  const csvEntities = assetClasses.map((ac) => ({
    id: ac.id,
    label: localize(ac.nameI18n),
  }));
  const hasDuplicateNames = new Set(csvEntities.map((e) => e.label)).size !== csvEntities.length;
  const csvFileInputRef = useRef<HTMLInputElement>(null);

  const handleDownloadCsv = () => {
    const csv = buildCorrelationCsv(csvEntities, storedCorrelations);
    downloadCsv(csv, "asset-class-correlations.csv");
  };

  const handleUploadCsvFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (csvFileInputRef.current) csvFileInputRef.current.value = "";
    if (!file) return;

    setError(null);
    setSuccess(false);

    const text = await file.text();
    const parsed = parseCorrelationCsv(text, csvEntities);
    if (parsed.error !== null) {
      setError(`${t.failedToParseCsv}: ${parsed.error}`);
      return;
    }

    saveCorrelations.mutate(
      {
        collectionId: portfolioCollectionId,
        data: {
          matrix: buildCorrelationPayload(
            // The entities the CSV carries (every asset class): a pair the
            // file leaves blank is cleared.
            csvEntities.map((e) => e.id),
            parsed.matrix,
            { clearMissing: true },
          ),
        },
      },
      {
        onSuccess: () => {
          setSuccess(true);
          setSuccessMessage(t.csvUploaded);
          setTimeout(() => setSuccess(false), 3000);
        },
        onError: (err: Error) => {
          setError(err.message || t.failedToSave);
        },
      },
    );
  };

  const isPending = saveCorrelations.isPending || fetchApiCorrelations.isPending;
  const saving = saveCorrelations.isPending;
  const apiLoading = fetchApiCorrelations.isPending;

  // Check if we have enough linked asset classes
  const hasEnoughLinked = linkedAssetClasses.length >= 2;
  const hasApiCorrelations = Object.keys(apiCorrelations).length > 0;

  // QP-164 — above the threshold, the grid is hidden behind a status summary.
  const isCollapsed = assetClasses.length > COLLAPSE_THRESHOLD;
  const requiredPairs = (assetClasses.length * (assetClasses.length - 1)) / 2;
  let definedPairs = 0;
  if (isCollapsed) {
    for (let i = 0; i < assetClasses.length; i++) {
      for (let j = i + 1; j < assetClasses.length; j++) {
        const aId = assetClasses[i]!.id;
        const bId = assetClasses[j]!.id;
        const value =
          getCorrelation(storedCorrelations, aId, bId) ??
          getCorrelation(storedCorrelations, bId, aId);
        if (isInRange(value) && value !== null) definedPairs++;
      }
    }
  }

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin mr-2" />
          <span>{t.loadingCorrelations}</span>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardDescription>{t.description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <div className="flex items-center gap-2 p-3 bg-destructive/10 text-destructive rounded-md text-sm">
            <AlertCircle className="h-4 w-4" />
            {error}
          </div>
        )}

        {success && (
          <div className="text-sm text-success bg-success/10 p-3 rounded-md">{successMessage}</div>
        )}

        {hasDuplicateNames && (
          <div className="flex items-center gap-2 p-3 bg-destructive/10 text-destructive rounded-md text-sm">
            <AlertCircle className="h-4 w-4" />
            {t.duplicateNamesBlockCsv}
          </div>
        )}

        {/* QP-164 — CSV export/import, available at any asset-class count. */}
        <div className="flex items-center justify-end gap-2">
          <input
            ref={csvFileInputRef}
            type="file"
            accept=".csv,text/csv"
            onChange={handleUploadCsvFile}
            className="hidden"
          />
          <Button
            variant="outline"
            size="sm"
            onClick={handleDownloadCsv}
            disabled={hasDuplicateNames}
          >
            {t.downloadCsv}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => csvFileInputRef.current?.click()}
            disabled={hasDuplicateNames || saveCorrelations.isPending}
          >
            {t.uploadCsv}
          </Button>
        </div>

        {isCollapsed ? (
          // QP-164 — above the threshold, the grid is replaced by a status
          // summary; CSV upload/download (above) is the bulk-editing path.
          <div className="space-y-2 rounded-md border p-4">
            <p className="text-sm font-medium">{t.collapsedTitle}</p>
            <p className="text-sm text-muted-foreground">
              {t.collapsedReason(assetClasses.length, COLLAPSE_THRESHOLD)}
            </p>
            <p className="text-sm font-medium">
              {t.correlationsStatus(definedPairs, requiredPairs)}
            </p>
            <p className="text-xs text-muted-foreground">{t.correlationsStatusHint}</p>
          </div>
        ) : (
          <>
            {/* Action buttons */}
            <div className="flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                {hasEnoughLinked
                  ? `${linkedAssetClasses.length} ${t.linkedAssetClasses}`
                  : t.linkInstrumentsHint}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleFetchApiCorrelations}
                  disabled={!hasEnoughLinked || isPending}
                  className="gap-2"
                  loading={apiLoading}
                >
                  {t.fetchFromApi}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCopyAllFromApi}
                  disabled={!hasApiCorrelations || isPending}
                >
                  {t.copyAll}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCopyEmptyFromApi}
                  disabled={!hasApiCorrelations || isPending}
                >
                  {t.copyEmpty}
                </Button>
                <Button size="sm" onClick={handleSave} disabled={isPending} loading={saving}>
                  {t.save}
                </Button>
              </div>
            </div>

            {apiError && (
              <div className="flex items-center gap-2 p-2 bg-destructive/10 text-destructive rounded-md text-sm">
                <AlertCircle className="h-4 w-4" />
                {apiError}
              </div>
            )}

            {/* Correlation Matrix Table */}
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr>
                    <th className="p-2 text-left border-b">{t.assetClass}</th>
                    {assetClasses.map((ac, idx) => (
                      <th key={ac.id} className="p-2 text-center border-b min-w-[100px]">
                        <div className="flex items-center justify-center">
                          <div
                            className={`w-4 h-4 rounded-full ${ASSET_CLASS_COLORS[idx % ASSET_CLASS_COLORS.length]}`}
                            title={localize(ac.nameI18n)}
                          />
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {assetClasses.map((rowAc, rowIdx) => (
                    <tr key={rowAc.id}>
                      <td className="p-2 border-b">
                        <div className="flex items-center gap-2">
                          <div
                            className={`w-3 h-3 rounded-full ${ASSET_CLASS_COLORS[rowIdx % ASSET_CLASS_COLORS.length]}`}
                          />
                          <span className="text-sm font-medium">{localize(rowAc.nameI18n)}</span>
                        </div>
                      </td>
                      {assetClasses.map((colAc, colIdx) => {
                        const isDiagonal = rowAc.id === colAc.id;
                        const isUpperTriangle = rowIdx < colIdx;

                        // For lower triangle, use row->col; for upper triangle, mirror from col->row
                        const apiValue = isUpperTriangle
                          ? getCorrelation(apiCorrelations, colAc.id, rowAc.id)
                          : getCorrelation(apiCorrelations, rowAc.id, colAc.id);
                        const storedValue = isUpperTriangle
                          ? getCorrelation(storedCorrelations, colAc.id, rowAc.id)
                          : getCorrelation(storedCorrelations, rowAc.id, colAc.id);

                        return (
                          <td
                            key={colAc.id}
                            className={`p-2 border-b text-center ${isDiagonal ? "bg-muted/50" : ""} ${isUpperTriangle ? "bg-muted/30" : ""}`}
                          >
                            {isDiagonal ? (
                              // Diagonal is always 1
                              <span className="text-xs text-muted-foreground">1.00</span>
                            ) : isUpperTriangle ? (
                              // Upper triangle: read-only, mirrored from lower triangle
                              <div className="flex flex-col gap-1">
                                <div className="text-xs text-muted-foreground">
                                  {apiLoading
                                    ? "..."
                                    : apiValue !== null
                                      ? apiValue.toFixed(2)
                                      : "-"}
                                </div>
                                <span className="text-xs text-muted-foreground">
                                  {storedValue !== null ? storedValue.toFixed(2) : "-"}
                                </span>
                              </div>
                            ) : (
                              // Lower triangle: editable
                              <div className="flex flex-col gap-1">
                                {/* API value */}
                                <div
                                  className={`text-xs ${
                                    apiValue !== null &&
                                    storedValue !== null &&
                                    Math.round(apiValue * 100) !== Math.round(storedValue * 100)
                                      ? "text-destructive"
                                      : "text-muted-foreground"
                                  }`}
                                >
                                  {apiLoading
                                    ? "..."
                                    : apiValue !== null
                                      ? apiValue.toFixed(2)
                                      : "-"}
                                </div>
                                {/* Input for stored value */}
                                <Input
                                  type="number"
                                  step="0.01"
                                  min="-1"
                                  max="1"
                                  className={cn(
                                    "w-20 h-7 text-xs text-center mx-auto",
                                    !isInRange(storedValue) &&
                                      "border-destructive focus-visible:ring-destructive",
                                  )}
                                  placeholder="0.00"
                                  value={storedValue !== null ? storedValue.toString() : ""}
                                  onChange={(e) =>
                                    handleCorrelationChange(rowAc.id, colAc.id, e.target.value)
                                  }
                                  disabled={isPending || saving}
                                />
                              </div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p className="text-xs text-muted-foreground">{t.helpText}</p>
          </>
        )}
      </CardContent>
    </Card>
  );
}
